/**
 * My requests — Step 71: every ticket where the signed-in requester is the
 * requester, shaped like the reference's ticket_list rows (subject + ref,
 * created/updated, assigned-to, status chip by the org's own status names).
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { portalApi } from '../../features/portal/api';
import { StatusChip } from '../../features/portal/StatusChip';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { relativeTime } from '../../lib/time';
import { qk } from '../../lib/queryClient';
import { ticketRef } from '@itsm/shared';

export function PortalTicketsPage() {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.portalTickets,
    queryFn: portalApi.myTickets,
  });
  const entries = data?.entries ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">My requests</h1>
        <div className="flex-1" />
        <Link to="/portal/catalogue">
          <Button size="sm">
            <Plus /> New request
          </Button>
        </Link>
      </div>

      {isError && (
        <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Could not load your requests</p>
          <p className="text-xs text-destructive/80">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="text-xs text-destructive/80 underline hover:text-destructive"
          >
            Retry
          </button>
        </div>
      )}
      {isPending && (
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      )}
      {data && entries.length === 0 && (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          You haven't raised anything yet.
        </div>
      )}
      {entries.length > 0 && (
        <div className="overflow-hidden rounded-lg border bg-card">
          {entries.map((t, i) => (
            <Link
              key={t.id}
              to={`/portal/requests/${t.id}`}
              className={
                'flex items-center gap-3 px-4 py-3 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' +
                (i > 0 ? ' border-t' : '')
              }
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {t.subject}{' '}
                  <span className="text-xs font-normal text-muted-foreground">
                    {ticketRef(t.type, t.number)}
                  </span>
                </span>
                <span className="block text-xs text-muted-foreground">
                  Updated {relativeTime(t.updatedAt)}
                  {t.assigneeName ? ` · Assigned to ${t.assigneeName}` : ''}
                </span>
              </span>
              <StatusChip status={t.status} category={t.statusCategory} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
